/**
 * How real the far piece of a cut looks — real walls and fixtures, or the
 * outline the scan only guessed at.
 *
 * Read BY LENGTH, not by edge count, and that distinction is load-bearing. Cut
 * a 6 m room 0.24 m in from its unwalled side and the far strip has three edges
 * besides the cut: two 0.24 m stubs where the room's own side walls run past
 * the new wall, and the 6 m guessed edge. Counted, that is two real edges
 * against one invented and the strip reads as a room. Measured, it is 92%
 * invented — and the side walls continuing past a wall say nothing about
 * whether the space beyond exists. What says so is the boundary FACING AWAY.
 *
 * Moved here from `room-gap/operation.js` so the chain split can apply the
 * same test without a require cycle (room-gap's operation imports the split's
 * eligibility). `room-gap/operation.js` re-exports both names.
 *
 * Pure.
 */

const { isPointInsideOrOnPolygon } = require('../geometry/pointInPolygon');
const { edgeCovered, MIN_GAP_EDGE_M } = require('../room-gap/runs');

const IN_POLYGON_TOL_M = 0.01;

/** A far piece whose boundary is at least this uncovered, by length, is the
 *  scan's guess rather than a space. */
const GUESSWORK_SHARE = 0.5;

/**
 * @param {{x,z}[]} piecePolygon the far piece
 * @param {object} room the room it was cut from (its `walls`, `objects`)
 * @param {(a, b) => boolean} isCutEdge true for an edge the cut itself made —
 *   the cut is not evidence of anything
 * @returns {{ coveredEdges, realEdges, objects, coveredLength, openLength,
 *             uncoveredShare }}
 */
function farSideEvidence(piecePolygon, room, isCutEdge) {
  let coveredEdges = 0;
  let realEdges = 0;
  let coveredLength = 0;
  let openLength = 0;
  for (let i = 0; i < piecePolygon.length; i++) {
    const a = piecePolygon[i];
    const b = piecePolygon[(i + 1) % piecePolygon.length];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < MIN_GAP_EDGE_M) continue;
    if (isCutEdge(a, b)) continue; // the cut itself is not evidence
    realEdges++;
    if (edgeCovered(a, b, room.walls || [])) {
      coveredEdges++;
      coveredLength += len;
    } else {
      openLength += len;
    }
  }
  const objects = (room.objects || []).filter((o) => {
    const p = o?.center || o?.position;
    return p && isPointInsideOrOnPolygon(p, piecePolygon, IN_POLYGON_TOL_M);
  }).length;
  const total = coveredLength + openLength;
  return {
    coveredEdges,
    realEdges,
    objects,
    coveredLength,
    openLength,
    uncoveredShare: total > 0 ? openLength / total : 0,
  };
}

/** True when the evidence says the piece is guesswork, not a space. */
function isGuesswork(evidence) {
  return evidence.objects === 0 && evidence.uncoveredShare >= GUESSWORK_SHARE;
}

module.exports = { GUESSWORK_SHARE, farSideEvidence, isGuesswork };
